import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Calendar, 
  Building2, 
  Clock,
  ArrowRight,
  X,
  Minus,
  ChevronDown,
  User
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TaskDetailsModal } from "@/components/tasks2/TaskDetailsModal";
import { Task2, Project } from "@/types/tasks";
import { useTasks } from "@/hooks/useTasks";

const getStatusColor = (priority: string, isOverdue: boolean) => {
  if (isOverdue) return 'bg-destructive text-destructive-foreground';
  
  switch (priority) {
    case 'high':
      return 'bg-destructive text-destructive-foreground';
    case 'medium': 
      return 'bg-warning text-warning-foreground';
    case 'low':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const getStatusLabel = (priority: string, isOverdue: boolean) => {
  if (isOverdue) return 'Atrasada';
  
  switch (priority) {
    case 'high':
      return 'Urgente';
    case 'medium':
      return 'Importante';
    case 'low':
      return 'Normal';
    default:
      return 'Pendente';
  }
};

const formatDeadline = (date: Date) => {
  const now = new Date();
  const diffTime = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return 'Atrasada';
  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Amanhã';
  if (diffDays <= 7) return `Em ${diffDays} dias`;
  
  return date.toLocaleDateString('pt-BR', { 
    weekday: 'short', 
    day: 'numeric', 
    month: 'short' 
  });
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'high':
      return 'border-l-destructive';
    case 'medium':
      return 'border-l-warning';
    case 'low':
      return 'border-l-muted-foreground';
    default:
      return 'border-l-muted';
  }
};

interface UpcomingTasksProps {
  isVisible?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
}

export const UpcomingTasks = ({ isVisible = true, onVisibilityChange }: UpcomingTasksProps) => {
  const navigate = useNavigate();
  const [isMinimized, setIsMinimized] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task2 | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAllTasksPopup, setShowAllTasksPopup] = useState(false);

  // Tarefas reais (Supabase via useTasks), ordenadas pelo prazo mais próximo
  const { tasks: tasksReais } = useTasks();
  const allTasks = [...tasksReais]
    .filter((t) => t.status !== "completed")
    .sort((a, b) => (a.endDate?.getTime() ?? Infinity) - (b.endDate?.getTime() ?? Infinity));
  const tasks = allTasks.slice(0, 5);
  const projects: Project[] = Array.from(
    new Map(
      tasksReais.map((t) => [t.projectId, { id: t.projectId, name: t.projectName, color: "#0071CE" }])
    ).values()
  );

  const handleTaskClick = (task: Task2) => {
    setSelectedTask(task);
    setShowTaskModal(true);
    // Keep the popup open when opening task details
  };

  const handleTaskUpdate = (updatedTask: Task2) => {
    // In a real app, this would update the task in the database
    console.log('Task updated:', updatedTask);
  };

  if (!isVisible) return null;

  return (
    <Card className="shadow-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-foreground">Próximas Tarefas</CardTitle>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setShowAllTasksPopup(true)}
          >
            Ver todas
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-11 w-11 p-0 sm:h-6 sm:w-6"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? <ChevronDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-11 w-11 p-0 sm:h-6 sm:w-6"
            onClick={() => onVisibilityChange?.(false)}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      {!isMinimized && (
        <CardContent className="space-y-3">
          {tasks.length === 0 && (
            <div className="py-6 flex flex-col items-center justify-center text-center text-sm text-muted-foreground gap-2">
              <Calendar className="h-8 w-8 opacity-40" />
              <span>Nenhuma tarefa pendente. Crie tarefas no Gestor de Tarefas.</span>
            </div>
          )}
          {tasks.map((task) => {
            const isOverdue = task.endDate && task.endDate < new Date();
            return (
              <div 
                key={task.id}
                className={`p-4 rounded-lg border-l-4 ${getPriorityColor(task.priority)} bg-card hover:bg-accent/50 transition-colors cursor-pointer`}
                onClick={() => handleTaskClick(task)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-foreground text-sm">
                    {task.title}
                  </h4>
                  <Badge 
                    variant="secondary"
                    className={`text-xs ${getStatusColor(task.priority, isOverdue || false)}`}
                  >
                    {getStatusLabel(task.priority, isOverdue || false)}
                  </Badge>
                </div>
                
                <div className="flex items-center text-xs text-muted-foreground mb-2">
                  <Building2 className="h-3 w-3 mr-1" />
                  <span>{task.projectName}</span>
                </div>
                
                <div className="flex items-center text-xs text-muted-foreground">
                  <Clock className="h-3 w-3 mr-1" />
                  <span>
                    {task.endDate ? formatDeadline(task.endDate) : 'Sem prazo'}
                  </span>
                </div>
              </div>
            );
          })}
          
          <Button 
            variant="outline" 
            className="w-full mt-4"
            onClick={() => navigate('/tarefas2')}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Ver agenda completa
          </Button>
        </CardContent>
      )}
      
      {/* All Tasks Popup */}
      <Dialog open={showAllTasksPopup} onOpenChange={setShowAllTasksPopup}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Todas as Tarefas</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowAllTasksPopup(false);
                  navigate('/tarefas2');
                }}
                className="gap-2 mr-3"
                style={{ marginRight: '0.3cm' }}
              >
                <Calendar className="h-4 w-4" />
                Ver agenda completa
              </Button>
            </div>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[65vh] space-y-2">
            {allTasks.slice(0, 20).map((task) => {
              const isOverdue = task.endDate && task.endDate < new Date();
              return (
                <div 
                  key={task.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => handleTaskClick(task)}
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className={`w-1 h-8 rounded-full ${getPriorityColor(task.priority).replace('border-l-', 'bg-')}`} />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-sm truncate">{task.title}</h4>
                        <Badge 
                          variant="secondary"
                          className={`text-xs ${getStatusColor(task.priority, isOverdue || false)}`}
                        >
                          {getStatusLabel(task.priority, isOverdue || false)}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <div className="flex items-center">
                          <Building2 className="h-3 w-3 mr-1" />
                          <span className="truncate">{task.projectName}</span>
                        </div>
                        
                        <div className="flex items-center">
                          <User className="h-3 w-3 mr-1" />
                          <span>{task.assignees[0]?.name || 'Não atribuído'}</span>
                        </div>
                        
                        <div className="flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          <span>{task.endDate ? formatDeadline(task.endDate) : 'Sem prazo'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Details Modal */}
      {selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          projects={projects}
          isOpen={showTaskModal}
          onClose={() => {
            setShowTaskModal(false);
            setSelectedTask(null);
          }}
          onUpdate={handleTaskUpdate}
          isFullscreen={isFullscreen}
          onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
        />
      )}
    </Card>
  );
};